import { makeAutoObservable, observable, action } from 'mobx';
import { STATUS_API } from 'src/config';
import { EmfitAPI } from 'src/services/BaseAPI';
import { DeviceStore } from './DeviceStore';
import type { AppDeviceSettings } from './DevicesStore';
import { SessionStore } from './SessionStore';

type State = {
  devices: DeviceStore[];
  devicesById: {
    [deviceId: string]: DeviceStore;
  };
  devicesBySerial: {
    [serial: string]: DeviceStore;
  };
  loading: boolean;
  error: string | null;
};

/**
 * EmfitStore - Device management store
 *
 * Follows qs2 pattern:
 * - Fetches device settings from API
 * - Creates DeviceStore instances
 * - Maintains indexed lookups (by ID and serial)
 * - Singleton instance
 */
class EmfitStoreClass {
  private state: State = observable({
    devices: [],
    devicesById: {},
    devicesBySerial: {},
    loading: false,
    error: null,
  });

  hasDevicesLoaded = false;

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  // Getters

  get devices(): DeviceStore[] {
    return this.state.devices;
  }

  get devicesById() {
    return this.state.devicesById;
  }

  get devicesBySerial() {
    return this.state.devicesBySerial;
  }

  get loading(): boolean {
    return this.state.loading;
  }

  get error(): string | null {
    return this.state.error;
  }

  // Methods

  /**
   * Fetch all devices from API and create DeviceStore instances
   * Follows qs2 pattern exactly
   */
  getAllDevices() {
    this.state.loading = true;
    this.state.error = null;

    // Master/account users see all their devices. Group users (including group admins) see only their group's devices.
    const endpoint = SessionStore.isMasterUser
      ? `${STATUS_API}/v1/device`
      : `${STATUS_API}/devices`;

    return EmfitAPI.get<AppDeviceSettings[]>(endpoint)
      .then(
        action((response) => {
          if (!response) return;

          this.hasDevicesLoaded = true;

          // Sort devices by name (like qs2)
          this.state.devices = response
            .sort((a, b) => a.device_name.localeCompare(b.device_name))
            .map((deviceSettings) => new DeviceStore(deviceSettings));

          // Index by device_id
          this.state.devicesById = this.state.devices.reduce(
            (acc, device) => {
              acc[device.deviceId] = device;
              return acc;
            },
            {} as State['devicesById']
          );

          // Index by serial_number, lower-cased.
          // WORKAROUND(serial-case): the device list sends serials in upper
          // case ("005F15") and group-user-dashboard in lower case
          // ("006cb0"), so an exact-case lookup never matched and the
          // dashboard counted nobody as in bed.
          this.state.devicesBySerial = this.state.devices.reduce(
            (acc, device) => {
              acc[device.serialNumber.toLowerCase()] = device;
              return acc;
            },
            {} as State['devicesBySerial']
          );

          this.state.loading = false;
        })
      )
      .catch(
        action((error) => {
          this.state.error =
            error instanceof Error ? error.message : 'Failed to fetch devices';
          this.state.loading = false;
          console.error('EmfitStore.getAllDevices error:', error);
        })
      );
  }

  /**
   * Get device by ID
   */
  getDeviceById(deviceId: string): DeviceStore | undefined {
    return this.state.devicesById[deviceId];
  }

  /**
   * Get device by serial number
   */
  getDeviceBySerial(serial: string): DeviceStore | undefined {
    return this.state.devicesBySerial[serial.toLowerCase()];
  }

  /**
   * Refresh devices from API
   */
  refresh() {
    return this.getAllDevices();
  }

  /**
   * Drops everything belonging to the current login. Called from
   * resetStores() on logout, so the next login starts empty.
   */
  reset(): void {
    this.state.devices = [];
    this.state.devicesById = {};
    this.state.devicesBySerial = {};
    this.state.loading = false;
    this.state.error = null;
    this.hasDevicesLoaded = false;
  }
}

export const EmfitStore = new EmfitStoreClass();
